/**
 * `window.MDBCheckout.mountInstallmentsMessaging(el, opts)` - the landing
 * page's door to Stripe's PaymentMethodMessagingElement.
 *
 * The LP's injected installments block (checkout-island-mount
 * getInstallmentsCtaScript) is plain, framework-free JS and cannot load
 * Stripe.js by itself. The island bundle already ships Stripe.js, so it exposes
 * this one imperative helper: mount the provider element into a node and report
 * back whether anything actually rendered.
 *
 * THE RENDERED FLAG NO LONGER GATES VISIBILITY (changed 2026-09-06). It used
 * to: the element paints nothing for a buyer Stripe deems ineligible, leaving a
 * ~9 px empty iframe, so the LP block revealed itself only on
 * `{ rendered: true }`. On Live it then never revealed - in a harness driving
 * both publishable keys with countryCode forced to "US", USD 19900 / 4999 and
 * Klarna / Afterpay / Affirm alone and together, the element always fired
 * `ready` and stayed 9 px empty. Visibility is now decided by geo alone
 * (installments-eligibility) and this mount is a BEST-EFFORT decoration of an
 * already-revealed block: when Stripe does paint, an eligible buyer gets the
 * provider's amounts above our control; when it does not, the block still shows
 * our own control and nothing is lost. `rendered` is returned for logging only.
 *
 * Nothing here is a money surface of our own: we hand Stripe the CHARGE amount
 * and the buyer's country and render whatever the provider returns. The
 * per-instalment figure and the APR are the provider's lending disclosure.
 *
 * Fail-soft everywhere: a missing flag, a missing publishable key, an unknown
 * country, a network error or a Stripe error all resolve `{ rendered: false }`,
 * which leaves the landing page exactly as it is without installments.
 */
import { loadStripe, type Stripe } from "@stripe/stripe-js";

import { fetchCurrencyData } from "@/lib/currency";
import { SUPPORTED_COUNTRIES } from "./InstallmentsMessaging";

export type InstallmentsMessagingOptions = {
  /** CHARGE amount in CHARGE-currency minor units (never a display amount). */
  amountCents: number;
  /** ISO currency of the charge. Defaults to USD (the charge currency of the
   *  Unlimited / Renewal plans). */
  currency?: string;
  /** Billing country when the caller knows it - the LP block passes the one
   *  `installmentsEligibility()` already resolved, so this path never re-reads
   *  the stale session cache. Otherwise the visitor's geo country is resolved
   *  here, and an unknown country mounts nothing. */
  countryCode?: string;
};

type MessagingConfig = { pk: string; enabled: boolean };

let configPromise: Promise<MessagingConfig> | null = null;
let stripePromise: Promise<Stripe | null> | null = null;

/** Publishable key + the API's installments flag, fetched once per page. */
function messagingConfig(): Promise<MessagingConfig> {
  if (configPromise) return configPromise;
  const cfg = (window.__CHECKOUT_CONFIG__ || {}) as { apiUrl?: string; productId?: string };
  const base = cfg.apiUrl || "https://crm-app.makedesignerbags.com";
  const pid = cfg.productId || "";
  configPromise = fetch(`${base}/api/v1/checkout/public-config?product_id=${encodeURIComponent(pid)}`)
    .then((r) => r.json())
    .then((j) => {
      const d = (j?.data || j || {}) as { stripe_publishable_key?: string; installments_enabled?: boolean };
      return { pk: d.stripe_publishable_key || "", enabled: !!d.installments_enabled };
    })
    .catch(() => ({ pk: "", enabled: false }));
  return configPromise;
}

/** Height of a messaging element that actually painted a line of copy. An
 *  ineligible buyer gets an empty ~9 px iframe, so anything at or under this
 *  is "nothing rendered". Reported, never acted on - see the header. */
const RENDERED_MIN_HEIGHT_PX = 12;

export async function mountInstallmentsMessaging(
  el: HTMLElement,
  opts: InstallmentsMessagingOptions,
): Promise<{ rendered: boolean }> {
  try {
    if (!el || !(opts?.amountCents > 0)) return { rendered: false };
    const { pk, enabled } = await messagingConfig();
    if (!enabled || !pk) return { rendered: false };

    let country = (opts.countryCode || "").trim().toUpperCase();
    if (!country) {
      const geo = await fetchCurrencyData().catch(() => null);
      country = (geo?.country || "").trim().toUpperCase();
    }
    if (!SUPPORTED_COUNTRIES.has(country)) return { rendered: false };

    if (!stripePromise) stripePromise = loadStripe(pk);
    const stripe = await stripePromise;
    if (!stripe) return { rendered: false };

    const element = stripe.elements().create("paymentMethodMessaging", {
      amount: opts.amountCents,
      currency: (opts.currency || "USD").toUpperCase() as "USD",
      paymentMethodTypes: ["klarna", "afterpay_clearpay"],
      countryCode: country as "US",
    });
    element.mount(el);

    return await new Promise<{ rendered: boolean }>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve({ rendered: el.getBoundingClientRect().height > RENDERED_MIN_HEIGHT_PX });
      };
      // `ready` fires once the iframe is laid out; give the frame a beat to
      // report its real height before measuring.
      element.on("ready", () => setTimeout(finish, 250));
      // Hard stop: a provider that never becomes ready must not leave the LP
      // waiting on a promise forever.
      setTimeout(finish, 3000);
    });
  } catch {
    return { rendered: false };
  }
}
